<template>
	<v-card-text class="solid-mesh-list-item">
		<v-card-actions class="py-0" :style="isEditing && !isRenamingMode ? 'background-color: #0064BA;' : ''">
			<!-- Visibility toggle -->
			<v-btn variant="text" icon size="24" @click="toggleVisibility" :disabled="isDisabledToggleVisibilityBtn">
				<v-icon>
					{{ solidMesh?.isShow ? "icon:View" : "icon:View-Off" }}
				</v-icon>
			</v-btn>
			<!-- Panel toggle -->
			<v-btn variant="text" icon size="36" @click="isOpen = !isOpen" class="ml-0" :disabled="isEditing">
				<v-icon> mdi-play {{ isOpen ? "mdi-rotate-90" : "" }} </v-icon>
			</v-btn>
			<!-- Solid mesh name -->
			<div class="d-flex solid-mesh-name-area">
				<v-icon v-if="solidMesh?.type" class="mr-1" style="opacity: 1;">
					{{ solidMeshIcon }}
				</v-icon>
				<solid-mesh-name @end-rename="isRenamingMode = false" :isRenamingMode="isRenamingMode"
					:solidMeshId="solidMesh?.id">
				</solid-mesh-name>
			</div>
			<!-- Search button -->
			<v-btn variant="text" @click="fitToView" size="24" :disabled="!solidMesh?.isShow" icon>
				<v-icon size="small">icon:Search</v-icon>
			</v-btn>
			<!-- Menu button -->
			<v-menu>
				<template v-slot:activator="{ props }">
					<v-btn variant="text" size="24" icon v-bind="props">
						<v-icon> icon:Overflow-Menu-Vertical </v-icon>
					</v-btn>
				</template>
				<v-list density="compact">
					<!-- Export -->
					<v-list-item :disabled="!solidMesh?.isLoaded" @click="exportByJson">
						<v-icon class="mr-4">icon:Export</v-icon>
						<span class="menu-item-label">{{ $t("EXPORT_AS") }}</span>
					</v-list-item>
					<!-- Edit -->
					<v-list-item @click="toggleLock" :disabled="isEditing">
						<v-icon class="mr-4">icon:Pencil</v-icon>
						<span class="menu-item-label">{{ $t("EDIT") }}</span>
					</v-list-item>
					<!-- Duplicate -->
					<v-list-item @click="duplicateSolidMesh" :disabled="isEditing">
						<v-icon class="mr-4">mdi-content-copy</v-icon>
						<span class="menu-item-label">{{ $t("DUPLICATE") }}</span>
					</v-list-item>
					<!-- Delete -->
					<v-list-item @click="deleteSolidMesh">
						<v-icon class="mr-4">icon:Trash-Can</v-icon>
						<span class="menu-item-label">{{ $t("REMOVE") }}</span>
					</v-list-item>
				</v-list>
			</v-menu>
		</v-card-actions>

		<!-- Solid mesh detail panel -->
		<div v-if="isOpen">
			<solid-mesh-panel ref="solidMeshPanel" :solidMeshId="solidMesh?.id" :isEditing="isEditing"></solid-mesh-panel>
			<solid-mesh-transform-panel v-if="shouldShowTransformPanel" :solidMeshId="solidMesh?.id" :isEditing="isEditing"
				@transform-reset="onTransformReset"></solid-mesh-transform-panel>
		</div>
	</v-card-text>
</template>

<script>
import { SIMPLE_STRUCTURE_TYPE } from "@/models/objects/solidMesh/types";
import { ObjectType } from "@/models/objects/types";
import * as lockUtils from "@/utils/lock-utils";
import dayjs from "dayjs";
import { mapActions, mapMutations } from "vuex";
import { getStructureTypeConfig } from "./SimpleStructureTypeConfig";
import SolidMeshName from "./SolidMeshName.vue";
import SolidMeshPanel from "./SolidMeshPanel.vue";
import SolidMeshTransformPanel from "./SolidMeshTransformPanel.vue";

export default {
	name: "SolidMeshListItem",
	components: {
		SolidMeshPanel,
		SolidMeshTransformPanel,
		SolidMeshName,
	},
	props: {
		solidMeshId: String,
	},
	data() {
		return {
			isRenamingMode: false,
			isDisabledToggleVisibilityBtn: false,
			isOpen: false,
		};
	},
	watch: {
		isEditMode(newVal) {
			// When edit mode is active, ensure per-item accordion is closed
			if (newVal) this.isOpen = false;
		},
	},
	computed: {
		solidMesh() {
			return this.$store.state.object.objects.find(
				(obj) =>
					obj.type === ObjectType.SOLID_MESH && obj.id === this.solidMeshId,
			);
		},
		isObjectUpdating() {
			return this.$store.state.isObjectUpdating;
		},
		isEditMode() {
			return this.$store.state.inProgressModeManager?.inProgressMode === "EDIT";
		},
		inProgressMode() {
			return this.$store.state.inProgressModeManager;
		},
		isEditing() {
			return (
				this.$store.state.object.objectUnderEdit?.id === this.solidMesh?.id
			);
		},
		shouldShowTransformPanel() {
			// Show transform panel for all structure types except manhole
			const structureType =
				this.solidMesh?.metaData?.attributes?.structureType ||
				this.solidMesh?.attributes?.structureType;
			return this.isEditing && structureType !== SIMPLE_STRUCTURE_TYPE.MANHOLE;
		},
		solidMeshIcon() {
			const structureType =
				this.solidMesh?.metaData?.attributes?.structureType ??
				this.solidMesh?.attributes?.structureType ??
				SIMPLE_STRUCTURE_TYPE.MANUAL;
			return getStructureTypeConfig(structureType)?.icon ?? "mdi-drawing-box";
		},
	},
	methods: {
		onTransformReset(resetRotation) {
			this.$refs.solidMeshPanel?.syncGradientFromReset(resetRotation);
		},
		...mapActions("object", ["startEdit", "endEdit", "deleteObject"]),
		...mapMutations(["set_snackbar"]),

		waitForUpdate(timeoutMs = 10000) {
			return new Promise((resolve, reject) => {
				if (!this.isObjectUpdating) {
					resolve();
				} else {
					const timer = setTimeout(() => {
						unwatch();
						reject(new Error("waitForUpdate timeout"));
					}, timeoutMs);
					const unwatch = this.$store.watch(
						(state) => state.isObjectUpdating,
						(newValue) => {
							if (!newValue) {
								clearTimeout(timer);
								unwatch();
								resolve();
							}
						},
					);
				}
			});
		},

		async toggleVisibility() {
			if (!this.solidMesh) return;

			if (this.solidMesh.isShow) {
				this.isDisabledToggleVisibilityBtn = true;
				try {
					await this.waitForUpdate();
				} finally {
					this.isDisabledToggleVisibilityBtn = false;
				}
			}

			try {
				await this.solidMesh.setVisibility(!this.solidMesh.isShow);
			} catch (e) {
				console.error(e);
				throw e;
			}
		},

		async toggleLock() {
			if (!this.solidMesh) return;

			if (!this.isEditing) {
				// Delegate to object store's startEdit to follow the standard object flow
				try {
					await this.startEdit(this.solidMesh);
				} catch (e) {
					console.error("Failed to start edit via object store:", e);
					// Show snackbar for user if needed
					this.set_snackbar({
						text: `${this.$t("EDIT")} ${this.$t("failed")}`,
						color: "rgba(153, 0, 0, 0.72)",
					});
				}
			} else {
				try {
					await this.endEdit();
				} catch (e) {
					console.error("Failed to end edit via object store:", e);
				}
			}
		},

		toggleRenamingMode() {
			this.isRenamingMode = !this.isRenamingMode;
		},

		fitToView() {
			if (!this.solidMesh) return;

			try {
				this.solidMesh.fitToView();
			} catch (err) {
				console.log("Something went wrong", err);
			}
		},

		exportByJson() {
			const geometry = this.solidMesh.getGeometry();
			const event = new CustomEvent("openPropertyDialog", {
				detail: {
					type: 14,
					name: this.solidMesh.name,
					geometry: geometry,
				},
			});
			window.dispatchEvent(event);
		},

		async duplicateSolidMesh() {
			if (!this.solidMesh) return;
			try {
				const siteId = this.$route.query.siteId;
				await this.$store.dispatch("object/duplicateObject", {
					siteId,
					obj: this.solidMesh,
				});
				this.set_snackbar({
					text: this.$t("DUPLICATION_SUCCESSFUL"),
					color: "rgba(0, 153, 0, 0.72)",
				});
			} catch (e) {
				console.error(e);
				if (e.message?.startsWith("LOCKED:")) {
					const lockUser = e.message.replace("LOCKED:", "");
					this.set_snackbar({
						text: this.$t("EXCLUSIVE_NOTICE", { lockUser }),
						color: "rgba(153, 0, 0, 0.72)",
					});
				} else {
					this.set_snackbar({
						text: this.$t("DUPLICATION_FAILED"),
						color: "rgba(153, 0, 0, 0.72)",
					});
				}
			}
		},

		async deleteSolidMesh() {
			if (!this.solidMesh) return;
			const solidMesh = this.solidMesh;

			try {
				const siteId = this.$route.query.siteId;
				const lockedObject = await lockUtils.checkLockStatus(
					siteId,
					solidMesh.id,
				);
				if (lockedObject.locked_by) {
					this.set_snackbar({
						text: this.$t("EXCLUSIVE_NOTICE", {
							lockUser: lockedObject.locked_by,
						}),
						color: "rgba(153, 0, 0, 0.72)",
					});
					return;
				}
				if (this.isEditing) {
					await solidMesh.finishEditInteraction?.();
					this.$store.commit("object/clearObjectUnderEdit");
					this.$store.commit("inProgressModeManager/clearInProgressMode");
					lockUtils.releaseLock();
				}
				await this.deleteObject({
					siteId,
					obj: solidMesh,
				});
				solidMesh.deleteObject();
				this.set_snackbar({
					text: `${this.$t("DELETE_SIMPLE_STRUCTURE")} ${this.$t("successful")}`,
					color: "rgba(0, 153, 0, 0.72)",
				});
			} catch (error) {
				console.error("Failed to delete solid mesh:", error);
				this.set_snackbar({
					text: `${this.$t("DELETE_SIMPLE_STRUCTURE")} ${this.$t("failed")}`,
					color: "rgba(153, 0, 0, 0.72)",
				});
			}
		},

		formatDate(dateString) {
			return dayjs(dateString).format("YYYY/MM/DD HH:mm");
		},
	},
};
</script>

<style scoped>
.solid-mesh-list-item {
	padding: 0;
	padding-left: 32px;
}

.solid-mesh-name-area {
	flex: 1;
	min-width: 0;
}

.menu-item-label {
	font-size: 13px;
}
</style>
